import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  User,
  Building2,
  Mail,
  Phone,
  Lock,
  Shield,
  AlertCircle,
  UserPlus,
  Upload,
  Eye,
  EyeOff,
  CreditCard,
  CheckCircle,
  FileText
} from 'lucide-react';
import { usersAPI, bondsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { toast } from 'sonner';
import { usePermissions } from '../../utils/permissionUtils';

const CreateUser = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Bank Account Information states
  const [selectedAccountTypes, setSelectedAccountTypes] = useState([]);
  const [bankAccountForms, setBankAccountForms] = useState({
    conventional: {
      bankName: '',
      accountName: '',
      accountNumber: '',
      accountCategory: 'current',
      swiftBic: '',
      branchCode: ''
    },
    islamic: {
      bankName: '',
      accountName: '',
      accountNumber: '',
      accountCategory: 'current',
      swiftBic: '',
      branchCode: '',
      shariahComplianceFile: null
    }
  });

  // Trustee Bond Assignment states
  const [selectedBond, setSelectedBond] = useState(null);

  // Bank options
  const bankOptions = [
    'Affin Bank', 'Agro Bank', 'Alliance Bank', 'Am Bank', 'Bank Islam',
    'Bank Muamalat', 'Bank Rakyat', 'Bank Simpanan National', 'CIMB Bank',
    'Hong Leong Bank', 'HSBC Bank', 'Kuwait Finance House', 'Maybank',
    'OCBC Bank', 'Public Bank', 'RHB Bank', 'Standard Chartered Bank', 'UOB Bank'
  ];

  // Check permissions

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      accountStatus: 'active',
      accountType: 'corporate', // Default to corporate since most admin-created users are institutional
      sendActivationEmail: true,
      forcePasswordChange: true
    }
  });

  const watchRole = watch('role');

  // Reset selected bond when role changes away from trustee
  useEffect(() => {
    if (watchRole !== 'trustee') {
      setSelectedBond(null);
    }
    // Reset selected account types when role changes away from custodian/investor/primary_subscriber
    if (!['custodian', 'investor', 'primary_subscriber'].includes(watchRole)) {
      setSelectedAccountTypes([]);
    }
  }, [watchRole]);

  // Fetch approved bonds for trustee role
  const { data: approvedBondsData, isLoading: bondsLoading } = useQuery({
    queryKey: ['approved-bonds', watchRole],
    queryFn: async () => {
      const response = await bondsAPI.getBonds({
        status: 'Approved,Listed,Active,Allocation Finalized',
        limit: 100 // API maximum limit is 100
      });
      const bonds = response?.data?.data?.bonds || response?.data?.data || response?.data || [];
      // Filter for approved/active bonds
      return bonds.filter(bond => 
        ['Approved', 'Listed', 'Active', 'Allocation Finalized'].includes(bond.status)
      );
    },
    enabled: watchRole === 'trustee'
  });

  const approvedBonds = approvedBondsData || [];

  const createUserMutation = useMutation({
    mutationFn: ({ userData, comments }) => {
      // If userData is FormData, send it directly (it already has all fields including actionType and comments)
      if (typeof FormData !== "undefined" && userData instanceof FormData) {
        console.log('Sending FormData directly to API');
        return usersAPI.submitApprovalRequest(userData);
      }
      // Otherwise, send as JSON object
      console.log('Sending JSON object to API');
      return usersAPI.submitApprovalRequest({
        actionType: 'create',
        userData: userData,
        comments: comments || 'User creation request submitted for L2 approval'
      });
    },
    onSuccess: () => {
      // Invalidate pending approvals query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['pendingApprovalRequests'] });
      // Also invalidate user-related queries
      queryClient.invalidateQueries({ queryKey: ['clientUsers'] });
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      queryClient.invalidateQueries({ queryKey: ['allUsersStats'] });
      
      toast.success('User creation request submitted successfully! You can track the approval status in the "Pending Approvals" tab.');
      navigate('/admin/users');
    },
    onError: (error) => {
      console.error('Submit approval request error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit approval request';

      // Show validation errors if available
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors
          .map(err => err.msg)
          .join('\n');
        toast.error(`Validation failed:\n${validationErrors}`);
      } else {
        toast.error(errorMessage);
      }
    }
  });

  // Page access is controlled by route permissions - no action-level check needed
  if (false) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const roles = [
    { value: '', label: 'Select Role...', disabled: true },
    { value: 'issuer', label: 'Issuer - Entity issuing tokenised bonds' },
    { value: 'custodian', label: 'Custodian/DAC - Manages investor onboarding, wallets, token distribution' },
    { value: 'primary_subscriber', label: 'Primary Subscriber - Direct allocation participant with wallet access' },
    { value: 'facility_agent', label: 'Facility Agent - Manages payments, maturities, and administrative tasks' },
    { value: 'super_admin', label: 'Super Admin - Full system administration access' },
    { value: 'investor', label: 'Investor - Investment and portfolio management' },
    { value: 'trustee', label: 'Trustee - Manages and monitors assigned bonds' }
  ];

  // Bank Account helper functions
  const handleAccountTypeSelection = (accountType, isSelected) => {
    // Prevent default behavior and page jumping
    if (isSelected) {
      setSelectedAccountTypes(prev => [...prev, accountType]);
      // Initialize form for new account type if not already exists
      if (!bankAccountForms[accountType]) {
        setBankAccountForms(prev => ({
          ...prev,
          [accountType]: {
            bankName: '',
            accountName: '',
            accountNumber: '',
            accountCategory: 'current',
            swiftBic: '',
            branchCode: '',
            shariahComplianceFile: accountType === 'islamic' ? null : undefined
          }
        }));
      }
    } else {
      setSelectedAccountTypes(prev => prev.filter(type => type !== accountType));
      // Clear the account data when unchecking
      setBankAccountForms(prev => ({
        ...prev,
        [accountType]: {
          bankName: '',
          accountName: '',
          accountNumber: '',
          accountCategory: 'current',
          swiftBic: '',
          branchCode: '',
          shariahComplianceFile: accountType === 'islamic' ? null : undefined
        }
      }));
    }

    // Prevent page jumping by maintaining scroll position
    setTimeout(() => {
      const bankAccountSection = document.querySelector('.bank-account-section');
      if (bankAccountSection) {
        bankAccountSection.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }, 100);
  };

  const handleBankAccountFormChange = (accountType, field, value) => {
    setBankAccountForms(prev => ({
      ...prev,
      [accountType]: {
        ...prev[accountType],
        [field]: value
      }
    }));
  };

  const handleShariahComplianceUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setBankAccountForms(prev => ({
        ...prev,
        islamic: {
          ...prev.islamic,
          shariahComplianceFile: file
        }
      }));
    }
  };

  // Check if bank account configuration is valid for roles that require it
  const isBankAccountValid = (role) => {
    if (!['custodian', 'investor', 'primary_subscriber'].includes(role)) {
      return true; // Bank account not required for this role
    }
    
    if (selectedAccountTypes.length === 0) {
      return false;
    }
    
    for (const accType of selectedAccountTypes) {
      const form = bankAccountForms[accType];
      if (!form?.bankName || !form?.accountName || !form?.accountNumber || !form?.accountCategory) {
        return false;
      }
      if (accType === 'islamic' && !form?.shariahComplianceFile) {
        return false;
      }
    }
    
    return true;
  };

  // Check if bond assignment is valid for trustee role
  const isBondAssignmentValid = (role) => {
    if (role !== 'trustee') {
      return true; // Bond assignment not required for other roles
    }
    
    return selectedBond !== null && selectedBond !== '';
  };

  // Get bank account field error message
  const getBankAccountError = (accountType, field) => {
    if (!formSubmitted) return null;
    const form = bankAccountForms[accountType];
    if (!form) return null;
    
    if (field === 'bankName' && !form.bankName) return 'Bank name is required';
    if (field === 'accountName' && !form.accountName) return 'Account name is required';
    if (field === 'accountNumber' && !form.accountNumber) return 'Account number is required';
    if (field === 'accountCategory' && !form.accountCategory) return 'Account category is required';
    if (field === 'shariahComplianceFile' && accountType === 'islamic' && !form.shariahComplianceFile) {
      return 'Shariah compliance declaration is required';
    }
    return null;
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    const formData = watch();
    
    // Basic required fields
    if (!formData.firstName?.trim()) return false;
    if (!formData.lastName?.trim()) return false;
    if (!formData.email?.trim()) return false;
    if (!formData.lei?.trim() || formData.lei.trim().length !== 20) return false;
    if (!formData.password) return false;
    if (!formData.role) return false;
    
    // Corporate information required fields
    if (!formData.legalEntityName?.trim()) return false;
    if (!formData.businessRegistrationNumber?.trim()) return false;
    if (!formData.registrationType) return false;
    
    // Bank account validation for specific roles
    if (!isBankAccountValid(formData.role)) return false;
    
    // Bond assignment validation for trustee role
    if (!isBondAssignmentValid(formData.role)) return false;
    
    return true;
  };

  const onSubmit = async (data) => {
    setFormSubmitted(true);
    
    // Check bank account validation for roles that require it
    if (!isBankAccountValid(data.role)) {
      toast.error('Please complete the Bank Account Configuration');
      return;
    }

    // Check bond assignment validation for trustee role
    if (data.role === 'trustee' && !selectedBond) {
      toast.error('Please select a bond for trustee assignment');
      return;
    }
    
    try {
      console.log('=== FORM SUBMISSION START ===');
      console.log('Raw form data from react-hook-form:', JSON.stringify(data, null, 2));
      
      const formData = data;
      
      // Validate that we have the required form fields
      if (!formData.email || !formData.role || !formData.password) {
        toast.error('Missing required fields. Please check: email, role, and password.');
        return;
      }

      // Prepare userData matching backend API expectations
      const accountType = formData.accountType || 'corporate';

      const userData = {
        email: formData.email?.trim() || '',
        password: formData.password || '',
        role: formData.role || '',
        lei: formData.lei?.trim().toUpperCase() || '',
        accountType: accountType,
        accountStatus: formData.accountStatus || 'active',
        sendActivationEmail: Boolean(formData.sendActivationEmail),
        forcePasswordChange: Boolean(formData.forcePasswordChange)
      };

      // Structure data according to account type as expected by backend
      if (accountType === 'retail') {
        userData.personalInfo = {};
        if (formData.firstName?.trim()) userData.personalInfo.firstName = formData.firstName.trim();
        if (formData.lastName?.trim()) userData.personalInfo.lastName = formData.lastName.trim();
        if (formData.phoneNumber?.trim()) userData.personalInfo.phoneNumber = formData.phoneNumber.trim();
      } else {
        userData.corporateInfo = {};

        // Backend expects companyName for validation, map legalEntityName to it
        if (formData.legalEntityName?.trim()) {
          userData.corporateInfo.companyName = formData.legalEntityName.trim();
          userData.corporateInfo.legalEntityName = formData.legalEntityName.trim();
        }

        // Add primary account holder name from firstName + lastName
        if (formData.firstName?.trim() && formData.lastName?.trim()) {
          userData.corporateInfo.primaryAccountHolderName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;
        }

        // Add other corporate fields
        userData.corporateInfo.businessRegistrationNumber = formData.businessRegistrationNumber?.trim() || '';
        userData.corporateInfo.registrationType = formData.registrationType?.trim() || '';
        userData.corporateInfo.dateOfIncorporation = formData.dateOfIncorporation?.trim() || '';
        userData.corporateInfo.registeredAddress = formData.registeredAddress?.trim() || '';
        userData.corporateInfo.operatingAddress = formData.operatingAddress?.trim() || '';
        userData.corporateInfo.phoneNumber = formData.phoneNumber?.trim() || '';
        userData.corporateInfo.website = formData.website?.trim() || '';
        userData.corporateInfo.businessNature = formData.businessNature?.trim() || '';
        userData.corporateInfo.taxIdNumber = formData.taxIdNumber?.trim() || '';
        userData.corporateInfo.annualRevenue = formData.annualRevenue?.trim() || '';
        userData.corporateInfo.numberOfEmployees = formData.numberOfEmployees?.trim() || '';
      }

      // Bank Account validation and data preparation for Custodian/Investor roles
      if (['custodian', 'investor', 'primary_subscriber'].includes(formData.role)) {
        if (selectedAccountTypes.length === 0) {
          toast.error('Please select at least one bank account type for Custodian/Investor roles');
          return;
        }

        // Validate bank account forms
        for (const accType of selectedAccountTypes) {
          const form = bankAccountForms[accType];
          if (!form.bankName || !form.accountName || !form.accountNumber || !form.accountCategory) {
            toast.error(`Please fill in all required fields for ${accType} bank account`);
            return;
          }

          // Validate Shariah compliance file for Islamic accounts
          if (accType === 'islamic' && !form.shariahComplianceFile) {
            toast.error('Shariah compliance declaration is required for Islamic accounts');
            return;
          }
        }

        // Prepare bank accounts data
        const bankAccounts = selectedAccountTypes.map(accType => {
          const form = bankAccountForms[accType];
          return {
            accountType: accType,
            bankName: form.bankName,
            accountName: form.accountName,
            accountNumber: form.accountNumber,
            accountCategory: form.accountCategory,
            swiftBic: form.swiftBic || '',
            branchCode: form.branchCode || '',
            shariahComplianceDocument: form.shariahComplianceFile?.name || null
          };
        });

        userData.bankAccounts = bankAccounts;
      }

      // Bond assignment for Trustee role
      if (formData.role === 'trustee') {
        if (!selectedBond) {
          toast.error('Please select a bond for trustee assignment');
          return;
        }
        // Send single bond ID for backend processing
        userData.assignedBonds = [{ 
          bondId: typeof selectedBond === 'string' ? selectedBond : selectedBond.toString() 
        }];
      }

      // Validate that we have the minimum required data
      if (!userData.email || !userData.role || !userData.password) {
        toast.error('Missing required fields: email, role, or password');
        return;
      }

      // Check if there's a shariah compliance file to upload
      const hasShariahFile = selectedAccountTypes.includes('islamic') && 
        bankAccountForms.islamic?.shariahComplianceFile;

      if (hasShariahFile) {
        const formDataObj = new FormData();
        formDataObj.append('userData', JSON.stringify(userData));
        formDataObj.append('actionType', 'create');
        formDataObj.append('comments', 'User creation request submitted for L2 approval');
        formDataObj.append('islamic_shariahCompliance', bankAccountForms.islamic.shariahComplianceFile);
        
        await createUserMutation.mutateAsync({ userData: formDataObj });
      } else {
        await createUserMutation.mutateAsync({ userData, comments: 'User creation request submitted for L2 approval' });
      }
    } catch (error) {
      // Error handling is done in mutation
    }
  };

  return (
    <>
      <style jsx>{`
        .bank-account-section {
          scroll-behavior: smooth;
          min-height: 200px;
        }
        .bank-account-option {
          transition: all 0.2s ease-in-out;
        }
        .bank-account-option:hover {
          transform: translateY(-1px);
        }
        .bank-account-option:active {
          transform: translateY(0);
        }
        .bank-account-forms {
          transition: all 0.3s ease-in-out;
        }
      `}</style>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center py-6">
              <button
                onClick={() => navigate('/admin/users')}
                className="mr-4 p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Create New User</h1>
                <p className="mt-2 text-sm text-gray-600">
                  Complete the form below to create a new user account
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Approval Workflow Notice */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Two-Level Approval Required
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>Your user creation request will be submitted for L2 approval. The user account will only be created once approved by another administrator.</p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* User Basic Information */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center">
                  <User className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">User Basic Information</h3>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Required user details for account creation.
                </p>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">
                      First Name *
                    </label>
                    <input
                      type="text"
                      className={`form-input ${errors.firstName ? 'form-input-error' : ''}`}
                      placeholder="Enter first name"
                      {...register('firstName', {
                        required: 'First name is required',
                        minLength: { value: 2, message: 'First name must be at least 2 characters' }
                      })}
                    />
                    {errors.firstName && (
                      <p className="form-error">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      className={`form-input ${errors.lastName ? 'form-input-error' : ''}`}
                      placeholder="Enter last name"
                      {...register('lastName', {
                        required: 'Last name is required',
                        minLength: { value: 2, message: 'Last name must be at least 2 characters' }
                      })}
                    />
                    {errors.lastName && (
                      <p className="form-error">{errors.lastName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="email"
                        className={`form-input pl-10 ${errors.email ? 'form-input-error' : ''}`}
                        placeholder="Enter email address"
                        {...register('email', {
                          required: 'Email address is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Please enter a valid email address'
                          }
                        })}
                      />
                    </div>
                    {errors.email && (
                      <p className="form-error">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      LEI (Legal Entity Identifier) *
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        className={`form-input pl-10 ${errors.lei ? 'form-input-error' : ''}`}
                        placeholder="Enter LEI (e.g., 529900T8BM49AURSDO55)"
                        maxLength={20}
                        {...register('lei', {
                          required: 'LEI (Legal Entity Identifier) is required',
                          minLength: { value: 20, message: 'LEI must be exactly 20 characters' },
                          maxLength: { value: 20, message: 'LEI must be exactly 20 characters' },
                          pattern: {
                            value: /^[A-Z0-9]{20}$/i,
                            message: 'LEI must contain only letters and numbers'
                          }
                        })}
                        onChange={(e) => {
                          e.target.value = e.target.value.toUpperCase();
                        }}
                      />
                    </div>
                    {errors.lei && (
                      <p className="form-error">{errors.lei.message}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      A 20-character alphanumeric code that uniquely identifies legal entities globally.
                    </p>
                  </div>

                  <div>
                    <label className="form-label">
                      Initial Password *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className={`form-input pl-10 pr-10 ${errors.password ? 'form-input-error' : ''}`}
                        placeholder="Enter initial password"
                        {...register('password', {
                          required: 'Password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters' },
                          pattern: {
                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                            message: 'Password must contain uppercase, lowercase, number, and special character'
                          }
                        })}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="form-error">{errors.password.message}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Status */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Account Status</h3>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Set the initial status for this user account.
                </p>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="active"
                      type="radio"
                      value="active"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      {...register('accountStatus')}
                    />
                    <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                      <span className="font-medium">Active</span> - User can login immediately
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="inactive"
                      type="radio"
                      value="inactive"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      {...register('accountStatus')}
                    />
                    <label htmlFor="inactive" className="ml-2 block text-sm text-gray-900">
                      <span className="font-medium">Inactive</span> - User account is disabled
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Type Selection */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Account Type</h3>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Choose whether this is an individual or corporate account.
                </p>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="corporate"
                      type="radio"
                      value="corporate"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      {...register('accountType')}
                    />
                    <label htmlFor="corporate" className="ml-2 block text-sm text-gray-900">
                      <span className="font-medium">Corporate</span> - Business/Institutional account (uses company information)
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Corporate Information */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Corporate Information</h3>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Business details for corporate verification.
                </p>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="form-label">Legal Entity Name *</label>
                    <input
                      type="text"
                      className={`form-input ${errors.legalEntityName ? 'form-input-error' : ''}`}
                      placeholder="Enter complete registered company name"
                      {...register('legalEntityName', {
                        required: 'Legal Entity Name is required'
                      })}
                    />
                    {errors.legalEntityName && (
                      <p className="form-error">{errors.legalEntityName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Business Registration Number *</label>
                    <input
                      type="text"
                      className={`form-input ${errors.businessRegistrationNumber ? 'form-input-error' : ''}`}
                      placeholder="Enter SSM registration number"
                      {...register('businessRegistrationNumber', {
                        required: 'Business Registration Number is required'
                      })}
                    />
                    {errors.businessRegistrationNumber && (
                      <p className="form-error">{errors.businessRegistrationNumber.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Registration Type *</label>
                    <select 
                      className={`form-input ${errors.registrationType ? 'form-input-error' : ''}`} 
                      {...register('registrationType', {
                        required: 'Registration Type is required'
                      })}
                    >
                      <option value="">Select registration type</option>
                      <option value="Sdn Bhd">Sdn Bhd</option>
                      <option value="Bhd">Bhd</option>
                      <option value="LLP">LLP</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Sole Proprietorship">Sole Proprietorship</option>
                    </select>
                    {errors.registrationType && (
                      <p className="form-error">{errors.registrationType.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Date of Incorporation</label>
                    <input
                      type="date"
                      className="form-input"
                      {...register('dateOfIncorporation')}
                    />
                  </div>

                  <div>
                    <label className="form-label">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="tel"
                        className="form-input pl-10"
                        placeholder="Enter company phone number"
                        {...register('phoneNumber')}
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="form-label">Registered Address</label>
                    <textarea
                      className="form-input"
                      rows="3"
                      placeholder="Enter SSM registered address"
                      {...register('registeredAddress')}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="form-label">Operating Address</label>
                    <textarea
                      className="form-input"
                      rows="3"
                      placeholder="Enter actual business operating address (if different from registered address)"
                      {...register('operatingAddress')}
                    />
                  </div>

                  <div>
                    <label className="form-label">Website</label>
                    <input
                      type="url"
                      className="form-input"
                      placeholder="Enter company website (optional)"
                      {...register('website')}
                    />
                  </div>

                  <div>
                    <label className="form-label">Business Nature</label>
                    <select className="form-input" {...register('businessNature')}>
                      <option value="">Select business nature</option>
                      <option value="Financial Services">Financial Services</option>
                      <option value="Technology">Technology</option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Real Estate">Real Estate</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Education">Education</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Tax ID Number</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter tax identification number"
                      {...register('taxIdNumber')}
                    />
                  </div>

                  <div>
                    <label className="form-label">Annual Revenue Range</label>
                    <select className="form-input" {...register('annualRevenue')}>
                      <option value="">Select annual revenue range</option>
                      <option value="Below 1M">Below RM 1,000,000</option>
                      <option value="1M-5M">RM 1,000,000 - 5,000,000</option>
                      <option value="5M-25M">RM 5,000,000 - 25,000,000</option>
                      <option value="25M-100M">RM 25,000,000 - 100,000,000</option>
                      <option value="Above 100M">Above RM 100,000,000</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Number of Employees</label>
                    <select className="form-input" {...register('numberOfEmployees')}>
                      <option value="">Select number of employees</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="Above 500">Above 500 employees</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Role & Permissions */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Role & Permissions</h3>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Assign the appropriate role and access level for this user.
                </p>
              </div>
              <div className="card-body">
                <div>
                  <label className="form-label">
                    User Role *
                  </label>
                  <select
                    className={`form-input ${errors.role ? 'form-input-error' : ''}`}
                    {...register('role', {
                      required: 'Please select a user role'
                    })}
                  >
                    {roles.map((role) => (
                      <option
                        key={role.value}
                        value={role.value}
                        disabled={role.disabled}
                      >
                        {role.label}
                      </option>
                    ))}
                  </select>
                  {errors.role && (
                    <p className="form-error">{errors.role.message}</p>
                  )}
                  {watchRole && (
                    <div className="mt-2 p-3 bg-blue-50 border-l-4 border-blue-400">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <AlertCircle className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-blue-700">
                            {roles.find(r => r.value === watchRole)?.label.split(' - ')[1]}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bond Assignment Configuration - Only for Trustee role */}
            {watchRole === 'trustee' && (
              <div className="card">
                <div className="card-header">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-blue-600 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">Bond Assignment</h3>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    Select an approved bond to assign to this trustee. The trustee will be able to view and manage this bond.
                  </p>
                </div>
                <div className="card-body">
                  {bondsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <LoadingSpinner size="sm" text="Loading approved bonds..." />
                    </div>
                  ) : approvedBonds.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-500">No approved bonds available for assignment</p>
                    </div>
                  ) : (
                    <div>
                      <label className="form-label">Select Bond *</label>
                      {formSubmitted && !selectedBond && (
                        <p className="form-error">Please select a bond</p>
                      )}
                      <div className="mt-2 border border-gray-300 rounded-md max-h-60 overflow-y-auto">
                        {approvedBonds.map((bond) => (
                          <div
                            key={bond._id}
                            className={`p-3 border-b border-gray-200 last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                              selectedBond === bond._id ? 'bg-blue-50 border-blue-200' : ''
                            }`}
                            onClick={() => {
                              setSelectedBond(bond._id);
                            }}
                          >
                            <div className="flex items-start">
                              <input
                                type="radio"
                                checked={selectedBond === bond._id}
                                onChange={() => setSelectedBond(bond._id)}
                                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <div className="ml-3 flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {bond.bondName || 'Unnamed Bond'}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {bond.isin && `ISIN: ${bond.isin}`}
                                  {bond.isin && bond.bondNumber && ' • '}
                                  {bond.bondNumber && `Bond #: ${bond.bondNumber}`}
                                  {bond.productType && ` • ${bond.productType}`}
                                </div>
                                <div className="mt-1">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    bond.status === 'Approved' ? 'bg-green-100 text-green-800' :
                                    bond.status === 'Listed' ? 'bg-green-100 text-green-800' :
                                    bond.status === 'Active' ? 'bg-green-100 text-green-800' :
                                    bond.status === 'Allocation Finalized' ? 'bg-blue-100 text-blue-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {bond.status}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {selectedBond && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                          <p className="text-sm text-blue-800">
                            <strong>1</strong> bond selected
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bank Account Configuration - Only for Custodian/Investor roles */}
            {['custodian', 'investor', 'primary_subscriber'].includes(watchRole) && (
              <div className="card bank-account-section">
                <div className="card-header">
                  <div className="flex items-center">
                    <CreditCard className="h-5 w-5 text-blue-600 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">Bank Account Configuration</h3>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    Select bank account types and provide account details. Wallets will be automatically created based on your selection.
                  </p>
                </div>
                <div className="card-body">
                  {/* Bank Account Type Selection */}
                  <div className="space-y-4">
                    <label className="form-label">Bank Account Type *</label>
                    {formSubmitted && selectedAccountTypes.length === 0 && (
                      <p className="form-error">Please select at least one bank account type</p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        className={`bank-account-option border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedAccountTypes.includes('conventional')
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-primary-300'
                          }`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleAccountTypeSelection('conventional', !selectedAccountTypes.includes('conventional'));
                        }}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-4 h-4 rounded border-2 mt-1 ${selectedAccountTypes.includes('conventional')
                            ? 'border-primary-500 bg-primary-500'
                            : 'border-gray-300'
                            }`}>
                            {selectedAccountTypes.includes('conventional') && (
                              <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">Conventional Account</h4>
                            <p className="text-sm text-gray-600">Traditional banking account</p>
                          </div>
                        </div>
                      </div>

                      <div
                        className={`bank-account-option border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedAccountTypes.includes('islamic')
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-primary-300'
                          }`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleAccountTypeSelection('islamic', !selectedAccountTypes.includes('islamic'));
                        }}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-4 h-4 rounded border-2 mt-1 ${selectedAccountTypes.includes('islamic')
                            ? 'border-primary-500 bg-primary-500'
                            : 'border-gray-300'
                            }`}>
                            {selectedAccountTypes.includes('islamic') && (
                              <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">Islamic Account</h4>
                            <p className="text-sm text-gray-600">Shariah-compliant banking account</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bank Account Details */}
                  <div className="bank-account-forms">
                    {selectedAccountTypes.map((accountType) => (
                      <div key={accountType} className="border border-gray-200 rounded-lg p-6 mt-6">
                        <h4 className="font-medium text-gray-900 mb-4 capitalize">
                          {accountType} Account Details
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="form-label">Bank Name *</label>
                            <select
                              value={bankAccountForms[accountType]?.bankName || ''}
                              onChange={(e) => handleBankAccountFormChange(accountType, 'bankName', e.target.value)}
                              className={`form-input ${getBankAccountError(accountType, 'bankName') ? 'form-input-error' : ''}`}
                            >
                              <option value="">Select bank</option>
                              {bankOptions.map(bank => (
                                <option key={bank} value={bank}>{bank}</option>
                              ))}
                            </select>
                            {getBankAccountError(accountType, 'bankName') && (
                              <p className="form-error">{getBankAccountError(accountType, 'bankName')}</p>
                            )}
                          </div>

                          <div>
                            <label className="form-label">Account Name *</label>
                            <input
                              type="text"
                              value={bankAccountForms[accountType]?.accountName || ''}
                              onChange={(e) => handleBankAccountFormChange(accountType, 'accountName', e.target.value)}
                              className={`form-input ${getBankAccountError(accountType, 'accountName') ? 'form-input-error' : ''}`}
                              placeholder="Enter account holder name"
                            />
                            {getBankAccountError(accountType, 'accountName') && (
                              <p className="form-error">{getBankAccountError(accountType, 'accountName')}</p>
                            )}
                          </div>

                          <div>
                            <label className="form-label">Account Number *</label>
                            <input
                              type="text"
                              value={bankAccountForms[accountType]?.accountNumber || ''}
                              onChange={(e) => handleBankAccountFormChange(accountType, 'accountNumber', e.target.value)}
                              className={`form-input ${getBankAccountError(accountType, 'accountNumber') ? 'form-input-error' : ''}`}
                              placeholder="Enter account number"
                            />
                            {getBankAccountError(accountType, 'accountNumber') && (
                              <p className="form-error">{getBankAccountError(accountType, 'accountNumber')}</p>
                            )}
                          </div>

                          <div>
                            <label className="form-label">Account Category *</label>
                            <select
                              value={bankAccountForms[accountType]?.accountCategory || 'current'}
                              onChange={(e) => handleBankAccountFormChange(accountType, 'accountCategory', e.target.value)}
                              className={`form-input ${getBankAccountError(accountType, 'accountCategory') ? 'form-input-error' : ''}`}
                            >
                              <option value="current">Current</option>
                              <option value="savings">Savings</option>
                            </select>
                            {getBankAccountError(accountType, 'accountCategory') && (
                              <p className="form-error">{getBankAccountError(accountType, 'accountCategory')}</p>
                            )}
                          </div>

                          <div>
                            <label className="form-label">SWIFT / BIC (Optional)</label>
                            <input
                              type="text"
                              value={bankAccountForms[accountType]?.swiftBic || ''}
                              onChange={(e) => handleBankAccountFormChange(accountType, 'swiftBic', e.target.value)}
                              className="form-input"
                              placeholder="Enter SWIFT/BIC code"
                            />
                          </div>

                          <div>
                            <label className="form-label">Branch Code (Optional)</label>
                            <input
                              type="text"
                              value={bankAccountForms[accountType]?.branchCode || ''}
                              onChange={(e) => handleBankAccountFormChange(accountType, 'branchCode', e.target.value)}
                              className="form-input"
                              placeholder="Enter branch code"
                            />
                          </div>
                        </div>

                        {/* Shariah Compliance Declaration for Islamic accounts */}
                        {accountType === 'islamic' && (
                          <div className="mt-4">
                            <label className="form-label">Shariah Compliance Declaration *</label>
                            <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                              getBankAccountError(accountType, 'shariahComplianceFile') 
                                ? 'border-red-300 hover:border-red-500' 
                                : 'border-gray-300 hover:border-primary-500'
                            }`}>
                              <input
                                type="file"
                                accept=".pdf"
                                className="hidden"
                                id={`islamic_shariahCompliance`}
                                onChange={handleShariahComplianceUpload}
                              />
                              <label htmlFor={`islamic_shariahCompliance`} className="cursor-pointer">
                                <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                                <p className="text-sm text-gray-600">
                                  {bankAccountForms.islamic?.shariahComplianceFile ?
                                    bankAccountForms.islamic.shariahComplianceFile.name :
                                    'Upload Shariah Compliance Declaration'
                                  }
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Supports PDF (max 10MB)
                                </p>
                              </label>
                            </div>
                            {getBankAccountError(accountType, 'shariahComplianceFile') && (
                              <p className="form-error">{getBankAccountError(accountType, 'shariahComplianceFile')}</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Wallet Creation Notice */}
                  {selectedAccountTypes.length > 0 && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <CheckCircle className="h-5 w-5 text-green-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-green-800">
                            Automatic Wallet Creation
                          </h3>
                          <div className="mt-2 text-sm text-green-700">
                            <p>
                              {selectedAccountTypes.length === 1
                                ? `A ${selectedAccountTypes[0]} wallet will be automatically created for this user.`
                                : `${selectedAccountTypes.length} wallets (${selectedAccountTypes.join(' and ')}) will be automatically created for this user.`
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Account Security Settings */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center">
                  <Lock className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Account Security Settings</h3>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Configure additional security options for this user account.
                </p>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="forcePasswordChange"
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      {...register('forcePasswordChange')}
                    />
                    <label htmlFor="forcePasswordChange" className="ml-2 block text-sm text-gray-900">
                      Force password change on first login
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      id="sendActivationEmail"
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      {...register('sendActivationEmail')}
                    />
                    <label htmlFor="sendActivationEmail" className="ml-2 text-sm text-gray-900">
                      Send activation email to user
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/admin/users')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createUserMutation.isPending || !isFormValid()}
                className={`btn btn-primary ${!isFormValid() ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {createUserMutation.isPending ? (
                  <LoadingSpinner size="sm" text="Submitting..." />
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Submit for Approval
                  </>
                )}
              </button>
            </div>

            {/* Error Display */}
            {createUserMutation.error && (
              <div className="card border-red-200 bg-red-50">
                <div className="card-body">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Failed to submit approval request
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        {createUserMutation.error?.response?.data?.message ||
                          'An error occurred while submitting the approval request. Please try again.'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateUser; 